import { useState } from 'react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

export default function BalanceSheet() {
  const [asOfDate, setAsOfDate] = useState('2026-01-31')

  // Demo data
  const report = {
    assets: {
      currentAssets: {
        cash: 15680,
        checkingAccount: 32500,
        accountsReceivable: 2450,
        foodInventory: 8500,
        beverageInventory: 3200,
      },
      totalCurrentAssets: 62330,
      fixedAssets: {
        equipment: 45000,
        furniture: 12000,
        accumulatedDepreciation: -15000,
      },
      totalFixedAssets: 42000,
      totalAssets: 104330,
    },
    liabilities: {
      currentLiabilities: {
        accountsPayable: 12450,
        salesTaxPayable: 2340,
        tipsPayable: 890,
        accruedExpenses: 3200,
      },
      totalCurrentLiabilities: 18880,
      longTermLiabilities: {
        equipmentLoan: 25000,
      },
      totalLongTermLiabilities: 25000,
      totalLiabilities: 43880,
    },
    equity: {
      ownersEquity: 40092,
      retainedEarnings: 20358,
      totalEquity: 60450,
    },
    totalLiabilitiesAndEquity: 104330,
  }

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Balance Sheet</h1>
          <p className="text-gray-500 mt-1">Financial position at a point in time</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">As of:</span>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="input py-1.5"
            />
          </div>
          <Button variant="secondary">Export PDF</Button>
        </div>
      </div>

      {/* Report */}
      <Card className="max-w-3xl">
        <div className="text-center mb-8 pb-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Balance Sheet</h2>
          <p className="text-gray-500 mt-1">As of {asOfDate}</p>
        </div>

        <div className="space-y-8">
          {/* Assets */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Assets
            </h3>
            
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Assets</h4>
              <div className="pl-4 space-y-2">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Cash</span>
                  <span>{formatCurrency(report.assets.currentAssets.cash)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Checking Account</span>
                  <span>{formatCurrency(report.assets.currentAssets.checkingAccount)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Accounts Receivable</span>
                  <span>{formatCurrency(report.assets.currentAssets.accountsReceivable)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Food Inventory</span>
                  <span>{formatCurrency(report.assets.currentAssets.foodInventory)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Beverage Inventory</span>
                  <span>{formatCurrency(report.assets.currentAssets.beverageInventory)}</span>
                </div>
              </div>
              <div className="flex justify-between py-2 pl-4 font-medium border-t border-gray-100 mt-2">
                <span>Total Current Assets</span>
                <span>{formatCurrency(report.assets.totalCurrentAssets)}</span>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Fixed Assets</h4>
              <div className="pl-4 space-y-2">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Equipment</span>
                  <span>{formatCurrency(report.assets.fixedAssets.equipment)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Furniture & Fixtures</span>
                  <span>{formatCurrency(report.assets.fixedAssets.furniture)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Less: Accumulated Depreciation</span>
                  <span>{formatCurrency(report.assets.fixedAssets.accumulatedDepreciation)}</span>
                </div>
              </div>
              <div className="flex justify-between py-2 pl-4 font-medium border-t border-gray-100 mt-2">
                <span>Total Fixed Assets</span>
                <span>{formatCurrency(report.assets.totalFixedAssets)}</span>
              </div>
            </div>

            <div className="flex justify-between py-3 bg-gray-50 -mx-6 px-6 font-bold">
              <span>Total Assets</span>
              <span>{formatCurrency(report.assets.totalAssets)}</span>
            </div>
          </section>

          {/* Liabilities */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Liabilities
            </h3>
            
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Liabilities</h4>
              <div className="pl-4 space-y-2">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Accounts Payable</span>
                  <span>{formatCurrency(report.liabilities.currentLiabilities.accountsPayable)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Sales Tax Payable</span>
                  <span>{formatCurrency(report.liabilities.currentLiabilities.salesTaxPayable)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Tips Payable</span>
                  <span>{formatCurrency(report.liabilities.currentLiabilities.tipsPayable)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Accrued Expenses</span>
                  <span>{formatCurrency(report.liabilities.currentLiabilities.accruedExpenses)}</span>
                </div>
              </div>
              <div className="flex justify-between py-2 pl-4 font-medium border-t border-gray-100 mt-2">
                <span>Total Current Liabilities</span>
                <span>{formatCurrency(report.liabilities.totalCurrentLiabilities)}</span>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Long-Term Liabilities</h4>
              <div className="pl-4 space-y-2">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Equipment Loan</span>
                  <span>{formatCurrency(report.liabilities.longTermLiabilities.equipmentLoan)}</span>
                </div>
              </div>
              <div className="flex justify-between py-2 pl-4 font-medium border-t border-gray-100 mt-2">
                <span>Total Long-Term Liabilities</span>
                <span>{formatCurrency(report.liabilities.totalLongTermLiabilities)}</span>
              </div>
            </div>

            <div className="flex justify-between py-3 bg-gray-50 -mx-6 px-6 font-bold">
              <span>Total Liabilities</span>
              <span>{formatCurrency(report.liabilities.totalLiabilities)}</span>
            </div>
          </section>

          {/* Equity */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Equity
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Owner's Equity</span>
                <span className="font-medium">{formatCurrency(report.equity.ownersEquity)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Retained Earnings</span>
                <span className="font-medium">{formatCurrency(report.equity.retainedEarnings)}</span>
              </div>
              <div className="flex justify-between py-3 bg-gray-50 -mx-6 px-6 font-bold">
                <span>Total Equity</span>
                <span>{formatCurrency(report.equity.totalEquity)}</span>
              </div>
            </div>
          </section>

          {/* Total */}
          <div className="flex justify-between py-4 bg-primary-50 -mx-6 px-6 font-bold text-lg rounded-b-lg">
            <span>Total Liabilities & Equity</span>
            <span className="text-primary-700">{formatCurrency(report.totalLiabilitiesAndEquity)}</span>
          </div>
        </div>
      </Card>
    </div>
  )
}
